import React from 'react';
import {
  Box,
  Button,
  Dialog,
  Typography,
} from '@mui/material';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';

/**
 * Forced "a new version is available, please refresh" dialog. Surfaced by
 * useNewVersionCheck when /version.json reports a deploy newer than the one
 * the user loaded. Cannot be dismissed (ESC + backdrop disabled) — the only
 * way out is the Refresh button.
 *
 * Stays centered + compact at every breakpoint (no fullScreen) so the modal
 * reads as a friendly notification rather than a hostile takeover.
 */
export default function NewVersionDialog({ open, onRefresh }) {
  const handleRefresh = () => {
    if (onRefresh) onRefresh();
    else window.location.reload();
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={() => {}}
      PaperProps={{
        elevation: 0,
        sx: {
          width: 'calc(100vw - 32px)',
          maxWidth: 420,
          borderRadius: 3,
          overflow: 'hidden',
          // Soft elevation that reads as floating, not boxy.
          boxShadow:
            '0 10px 30px -10px rgba(15, 23, 42, 0.18), 0 4px 12px -4px rgba(15, 23, 42, 0.10)',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {/* Hero strip — soft primary tint + floating icon badge */}
      <Box
        sx={{
          position: 'relative',
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 3, sm: 3.5 },
          pb: { xs: 2, sm: 2.5 },
          textAlign: 'center',
          background: (theme) =>
            `radial-gradient(circle at top, ${theme.palette.primary.light} 0%, transparent 70%)`,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 1.5,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            boxShadow: (theme) => `0 8px 20px -6px ${theme.palette.primary.main}80`,
          }}
        >
          <RocketLaunchRoundedIcon sx={{ fontSize: 28 }} />
        </Box>

        <Typography
          component="h2"
          sx={{
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: 'text.primary',
            lineHeight: 1.3,
          }}
        >
          Nouvelle version disponible
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 0.75,
            color: 'text.secondary',
            lineHeight: 1.5,
            maxWidth: 320,
            mx: 'auto',
          }}
        >
          FabTraque a été mis à jour. Rechargez pour profiter des dernières améliorations.
        </Typography>
      </Box>

      {/* Body: reassurance line */}
      <Box sx={{ px: { xs: 2.5, sm: 3 }, pb: 1 }}>
        <Box
          sx={{
            mt: 0.5,
            mb: 2,
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: 'success.main',
              flexShrink: 0,
            }}
          />
          <Typography
            variant="caption"
            sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.4 }}
          >
            Vos modifications enregistrées seront conservées.
          </Typography>
        </Box>
      </Box>

      {/* Action */}
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          pb: { xs: 'calc(20px + env(safe-area-inset-bottom, 0px))', sm: 3 },
        }}
      >
        <Button
          variant="contained"
          startIcon={<AutorenewRoundedIcon />}
          onClick={handleRefresh}
          fullWidth
          size="large"
          autoFocus
          sx={{
            py: 1.25,
            fontWeight: 600,
            borderRadius: 1.5,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          Rafraîchir la page
        </Button>
      </Box>
    </Dialog>
  );
}
